// CycleAdministration.jsx
import { useState, useEffect } from "react";
import axios from "axios";
import dayjs from "dayjs";
import {
  Box, Stack, Typography, Chip, Tooltip, IconButton,
  Collapse, TextField, MenuItem, Grid, CircularProgress, Alert,
  Divider, Snackbar, Dialog, DialogTitle, DialogContent, DialogActions,
} from "@mui/material";
import { useTheme, alpha } from "@mui/material/styles";
import {
  ChevronDown, ChevronRight, Pencil, Check, X, Trash2,
  Plus, Calendar, NotebookPen, Logs,
} from "lucide-react";

import CustomDialogSmall from "../../../../components/Dialog/DiaCompSmall";

import { useCycleDataContext } from "../CycleDataContext";
import { useTranslation } from "../../../../language";
import { API_URL } from "../../../../constants";
import ButtonsMain from "../../../../components/Button/ButtonMain";

// ─────────────────────────────────────────────────────────────────────────────
// Constants
// ─────────────────────────────────────────────────────────────────────────────

const PHASE_CONFIG = [
  { key: "menstrual",  labelKey: "cyc_phase_menstrual",  startKey: "menstrual_start_date",  endKey: "menstrual_end_date"  },
  { key: "follicular", labelKey: "cyc_phase_follicular", startKey: "follicular_start_date", endKey: "follicular_end_date" },
  { key: "ovulation",  labelKey: "cyc_phase_ovulation",  startKey: "ovulation_start_date",  endKey: "ovulation_end_date"  },
  { key: "luteal",     labelKey: "cyc_phase_luteal",     startKey: "luteal_start_date",     endKey: "luteal_end_date"     },
];

const MENSTRUAL_RED = "#c62828";

// Only these 3 boundaries can be manually adjusted via adjust-phases endpoint

// Same phase→color mapping as CycleTracker.jsx: menstrual keeps its dedicated
// red, the other three phases reuse tertiary/quartiary/primary rather than
// MUI's stock success/warning roles (this app's theme never overrides those,
// so they were silently rendering default MUI colors, not this app's palette).
function usePhaseColors() {
  const theme = useTheme();
  return {
    menstrual:  MENSTRUAL_RED,
    follicular: theme.palette.tertiary.main,
    ovulation:  theme.palette.quartiary.main,
    luteal:     theme.palette.primary.main,
  };
}

// Lighter red for text on dark backgrounds
const MENSTRUAL_RED_TEXT_DARK = "#ef5350";

// Buttons are controls, so they get the same glass treatment used on the
// Tracker tab, rather than a solid fill — kept as a plain function (not a
// hook) since every call site already has `isDark` in scope.
function glassButtonSx(isDark) {
  return {
    px: "28px",
    color: isDark ? "common.white" : "greydark.C900",
    bgcolor: isDark ? alpha("#fff", 0.08) : alpha("#fff", 0.55),
    borderColor: isDark ? "rgba(255,255,255,0.16)" : "rgba(255,255,255,0.7)",
    backdropFilter: "blur(20px) saturate(180%)",
    WebkitBackdropFilter: "blur(20px) saturate(180%)",
    "&:hover": {
      color: isDark ? "common.white" : "greydark.C900",
      bgcolor: isDark ? alpha("#fff", 0.13) : alpha("#fff", 0.7),
      borderColor: isDark ? "rgba(255,255,255,0.24)" : "rgba(255,255,255,0.85)",
      transform: "scale(1.04)",
    },
  };
}

// ─────────────────────────────────────────────────────────────────────────────
// Phase Timeline Bar — horizontal proportional strip
// ─────────────────────────────────────────────────────────────────────────────

function PhaseTimelineBar({ cycle, phaseColors, height = 10 }) {
  const theme = useTheme();
  const phases = PHASE_CONFIG.map(({ key, startKey, endKey }) => ({
    key,
    start: cycle[startKey],
    end:   cycle[endKey],
  }));

  const totalDays = phases.reduce((sum, { start, end }) => {
    if (!start || !end) return sum;
    return sum + end.diff(start, "day") + 1;
  }, 0);

  if (totalDays === 0) return null;

  const today = dayjs();
  const cycleStart = phases[0]?.start;
  const cycleEnd   = phases[phases.length - 1]?.end;
  const isInCycle  = !cycle.is_system_prediction && cycleStart && cycleEnd
    && !today.isBefore(cycleStart) && !today.isAfter(cycleEnd);
  const todayPct = isInCycle && totalDays > 1
    ? Math.min(Math.max((today.diff(cycleStart, "day") / (totalDays - 1)) * 100, 1), 99)
    : null;

  return (
    <Box sx={{ position: "relative", pt: "6px" }}>
      {/* Today marker — sits above the bar */}
      {todayPct !== null && (
        <>
          <Box sx={{
            position: "absolute",
            left: `${todayPct}%`,
            top: 0,
            width: 2,
            height: "100%",
            bgcolor: theme.palette.text.primary,
            transform: "translateX(-50%)",
            zIndex: 1,
            borderRadius: 1,
          }} />
          <Box sx={{
            position: "absolute",
            left: `${todayPct}%`,
            top: 0,
            width: 6,
            height: 6,
            borderRadius: "50%",
            bgcolor: theme.palette.text.primary,
            transform: "translateX(-50%)",
            zIndex: 2,
          }} />
        </>
      )}

      {/* Phase segments */}
      <Box sx={{ display: "flex", borderRadius: 1.5, overflow: "hidden", height }}>
        {phases.map(({ key, start, end }) => {
          if (!start || !end) return null;
          const days = end.diff(start, "day") + 1;
          const pct = (days / totalDays) * 100;
          const color = key === "menstrual" ? MENSTRUAL_RED : phaseColors[key];
          const isPredicted = cycle.is_system_prediction;
          const isCurrent = !today.isBefore(start) && !today.isAfter(end);

          return (
            <Tooltip
              key={key}
              title={`${key.charAt(0).toUpperCase() + key.slice(1)}: ${start.format("DD MMM")} – ${end.format("DD MMM")} (${days}d)`}
              placement="top"
              arrow
            >
              <Box sx={{
                width: `${pct}%`,
                bgcolor: isPredicted ? alpha(color, 0.2) : alpha(color, isCurrent ? 0.85 : 0.55),
                borderRight: `1px solid ${alpha(color, 0.15)}`,
                "&:last-child": { borderRight: "none" },
                cursor: "default",
                ...(isPredicted && {
                  background: `repeating-linear-gradient(
                    45deg,
                    ${alpha(color, 0.12)},
                    ${alpha(color, 0.12)} 3px,
                    ${alpha(color, 0.25)} 3px,
                    ${alpha(color, 0.25)} 6px
                  )`,
                }),
              }} />
            </Tooltip>
          );
        })}
      </Box>
    </Box>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Phase Symptom Dialog
// ─────────────────────────────────────────────────────────────────────────────

const CATEGORIES = [
  "physical", "emotional", "energy", "sleep", "appetite", "skin", "other",
];
const SEVERITY_LEVELS = [
  { value: 1, key: "cyc_symptom_sev_mild",     color: "success" },
  { value: 2, key: "cyc_symptom_sev_moderate", color: "warning" },
  { value: 3, key: "cyc_symptom_sev_severe",   color: "error"   },
];
const CAT_KEYS = {
  physical: "cyc_symptom_cat_physical", emotional: "cyc_symptom_cat_emotional",
  energy:   "cyc_symptom_cat_energy",   sleep:     "cyc_symptom_cat_sleep",
  appetite: "cyc_symptom_cat_appetite", skin:      "cyc_symptom_cat_skin",
  other:    "cyc_symptom_cat_other",
};

function PhaseSymptomDialog({ open, onClose, cycle, phaseKey, phaseLabel, phaseStart, phaseEnd }) {
  const theme = useTheme();
  const { t } = useTranslation();
  const isDark = theme.palette.mode === "dark";

  const [symptoms, setSymptoms]   = useState([]);
  const [loading, setLoading]     = useState(false);
  const [adding, setAdding]       = useState(false);
  const [saving, setSaving]       = useState(false);
  const [snackbar, setSnackbar]   = useState({ open: false, message: "", severity: "success" });
  const [form, setForm] = useState({
    date: "", category: "", symptom_name: "", severity: "", notes: "",
  });

  useEffect(() => {
    if (!open || !cycle?.id) return;
    setAdding(false);
    setForm({ date: phaseStart?.format("YYYY-MM-DD") || "", category: "", symptom_name: "", severity: "", notes: "" });
    fetchSymptoms();
  }, [open, cycle?.id, phaseKey]);

  const fetchSymptoms = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/api/cycles/${cycle.id}/phase-symptoms/`, {
        params: { phase: phaseKey },
      });
      setSymptoms(res.data || []);
    } catch {
      setSymptoms([]);
    } finally { setLoading(false); }
  };

  const handleAdd = async () => {
    setSaving(true);
    try {
      await axios.post(`${API_URL}/api/symptoms/`, {
        cycle:        cycle.id,
        date:         form.date,
        category:     form.category,
        symptom_name: form.symptom_name,
        severity:     Number(form.severity),
        ...(form.notes && { notes: form.notes }),
      });
      setSnackbar({ open: true, message: t("cyc_symptom_notify_added"), severity: "success" });
      setAdding(false);
      setForm({ date: phaseStart?.format("YYYY-MM-DD") || "", category: "", symptom_name: "", severity: "", notes: "" });
      fetchSymptoms();
    } catch (err) {
      setSnackbar({ open: true, message: err.response?.data?.detail || t("cyc_symptom_notify_add_fail"), severity: "error" });
    } finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    try {
      await axios.delete(`${API_URL}/api/symptoms/${id}/`);
      setSnackbar({ open: true, message: t("cyc_symptom_notify_deleted"), severity: "success" });
      fetchSymptoms();
    } catch {
      setSnackbar({ open: true, message: t("cyc_symptom_notify_delete_fail"), severity: "error" });
    }
  };

  const isFormValid = form.date && form.category && form.symptom_name && form.severity;

  return (
    <>
      <CustomDialogSmall
        open={open}
        onClose={onClose}
        title={`${phaseLabel} — ${phaseStart?.format("DD MMM") ?? "—"} → ${phaseEnd?.format("DD MMM") ?? "—"}`}
        content={
          <Box sx={{ pt: 1, minWidth: { sm: 400 } }}>

            {/* Existing symptoms */}
            {loading ? (
              <Box sx={{ py: 3, display: "flex", justifyContent: "center" }}>
                <CircularProgress size={24} />
              </Box>
            ) : symptoms.length === 0 && !adding ? (
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {t("cyc_symptom_log_empty")}
              </Typography>
            ) : (
              <Stack spacing={0.75} sx={{ mb: 2 }}>
                {symptoms.map((s) => {
                  const sev = SEVERITY_LEVELS.find((l) => l.value === s.severity);
                  return (
                    <Stack key={s.id} direction="row" alignItems="flex-start" spacing={1} sx={{
                      px: 1.5, py: 1, borderRadius: 2,
                      border: "0.5px solid", borderColor: "divider",
                      bgcolor: isDark ? alpha("#fff", 0.02) : "grey.50",
                    }}>
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap" useFlexGap>
                          <Typography variant="body2" fontWeight={600}>{s.symptom_name}</Typography>
                          <Chip label={t(CAT_KEYS[s.category] || s.category)} size="small"
                            variant="outlined" sx={{ height: 18, fontSize: "0.6rem", "& .MuiChip-label": { px: 0.75 } }} />
                          {sev && (
                            <Chip label={t(sev.key)} size="small" color={sev.color}
                              sx={{ height: 18, fontSize: "0.6rem", "& .MuiChip-label": { px: 0.75 } }} />
                          )}
                          <Typography variant="caption" color="text.secondary">
                            {dayjs(s.date).format("DD MMM")}
                          </Typography>
                        </Stack>
                        {s.notes && (
                          <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.25 }}>
                            {s.notes}
                          </Typography>
                        )}
                      </Box>
                      <Tooltip title={t("cyc_admin_delete_tooltip")}>
                        <IconButton size="small" color="error" onClick={() => handleDelete(s.id)}>
                          <Trash2 size={13} />
                        </IconButton>
                      </Tooltip>
                    </Stack>
                  );
                })}
              </Stack>
            )}

            {/* Add form */}
            {adding ? (
              <Box sx={{
                p: 1.5, borderRadius: 3,
                border: "1px solid", borderColor: "divider",
                bgcolor: isDark ? alpha("#fff", 0.02) : alpha(theme.palette.primary.main, 0.02),
              }}>
                <Grid container spacing={1.5}>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <TextField
                      type="date" label={t("cyc_symptom_date")} size="small" fullWidth
                      InputLabelProps={{ shrink: true }}
                      inputProps={{
                        min: phaseStart?.format("YYYY-MM-DD"),
                        max: phaseEnd?.format("YYYY-MM-DD"),
                      }}
                      value={form.date}
                      onChange={(e) => setForm({ ...form, date: e.target.value })}
                    />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <TextField select label={t("cyc_symptom_category")} size="small" fullWidth
                      value={form.category}
                      onChange={(e) => setForm({ ...form, category: e.target.value })}>
                      {CATEGORIES.map((c) => (
                        <MenuItem key={c} value={c}>{t(CAT_KEYS[c])}</MenuItem>
                      ))}
                    </TextField>
                  </Grid>
                  <Grid size={12}>
                    <TextField label={t("cyc_symptom_name")} size="small" fullWidth
                      value={form.symptom_name}
                      onChange={(e) => setForm({ ...form, symptom_name: e.target.value })}
                      inputProps={{ maxLength: 100 }}
                    />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <TextField select label={t("cyc_symptom_severity")} size="small" fullWidth
                      value={form.severity}
                      onChange={(e) => setForm({ ...form, severity: e.target.value })}>
                      {SEVERITY_LEVELS.map((l) => (
                        <MenuItem key={l.value} value={l.value}>{t(l.key)}</MenuItem>
                      ))}
                    </TextField>
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <TextField label={t("cyc_symptom_notes")} size="small" fullWidth
                      value={form.notes}
                      onChange={(e) => setForm({ ...form, notes: e.target.value })}
                    />
                  </Grid>
                </Grid>
                <Stack direction="row" spacing={1} justifyContent="flex-end" sx={{ mt: 1.5 }}>
                  <ButtonsMain name={t("cyc_cancel")} action={() => setAdding(false)} sx={glassButtonSx(isDark)} />
                  <ButtonsMain
                    name={saving ? t("cyc_saving") : t("cyc_symptom_save")}
                    action={handleAdd}
                    disabled={!isFormValid || saving}
                    sx={glassButtonSx(isDark)}
                  />
                </Stack>
              </Box>
            ) : (
              <ButtonsMain
                name={t("cyc_symptom_add_btn")}
                action={() => setAdding(true)}
                startIcon={<Plus size={14} />}
                sx={glassButtonSx(isDark)}
              />
            )}
          </Box>
        }
      />

      <Snackbar open={snackbar.open} autoHideDuration={4000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}>
        <Alert severity={snackbar.severity} sx={{ borderRadius: 2 }}>{snackbar.message}</Alert>
      </Snackbar>
    </>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Symptoms Report Dialog
// ─────────────────────────────────────────────────────────────────────────────

function SymptomsReportDialog({ open, onClose }) {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const { t } = useTranslation();
  const { cycles, symptoms, symptomsLoading, loadSymptoms, getPhaseForDate } = useCycleDataContext();

  useEffect(() => {
    if (open) loadSymptoms();
  }, [open]);

  // ── Stats ──
  const total = symptoms.length;
  const byCat = {};
  const bySev = { 1: 0, 2: 0, 3: 0 };
  symptoms.forEach((s) => {
    byCat[s.category] = (byCat[s.category] || 0) + 1;
    bySev[s.severity] = (bySev[s.severity] || 0) + 1;
  });
  const topCats = Object.entries(byCat)
    .sort((a, b) => b[1] - a[1])
    .slice(0, 5);

  // ── Group by cycle (real, non-predicted, newest first) ──
  const realCycles = [...cycles]
    .filter((c) => !c.is_system_prediction)
    .sort((a, b) => (b.cycle_start_date?.valueOf() || 0) - (a.cycle_start_date?.valueOf() || 0));

  const cycleGroups = realCycles.map((cycle) => {
    const cs = symptoms.filter((s) => {
      if (!s.date || !cycle.cycle_start_date || !cycle.luteal_end_date) return false;
      return !s.date.isBefore(cycle.cycle_start_date) && !s.date.isAfter(cycle.luteal_end_date);
    }).sort((a, b) => b.date?.valueOf() - a.date?.valueOf());
    return { cycle, symptoms: cs };
  }).filter((g) => g.symptoms.length > 0);

  const phaseColor = (phase) => {
    const colors = {
      menstrual:  isDark ? MENSTRUAL_RED_TEXT_DARK : MENSTRUAL_RED,
      follicular: theme.palette.tertiary.main,
      ovulation:  theme.palette.quartiary.main,
      luteal:     theme.palette.primary.main,
    };
    return colors[phase] ?? theme.palette.divider;
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm" scroll="paper"
      PaperProps={{ sx: { borderRadius: 4, bgcolor: isDark ? "greydark.C900" : "grey.C50" } }}>

      <DialogTitle sx={{ pb: 1, fontWeight: 700, fontSize: "1.1rem" }}>
        {t("cyc_report_title")}
      </DialogTitle>

      <DialogContent dividers sx={{ px: 3, py: 2 }}>
        {symptomsLoading ? (
          <Box sx={{ py: 6, display: "flex", justifyContent: "center" }}>
            <CircularProgress />
          </Box>
        ) : total === 0 ? (
          <Typography variant="body1" color="text.secondary" sx={{ py: 4, textAlign: "center" }}>
            {t("cyc_report_empty")}
          </Typography>
        ) : (
          <Stack spacing={3}>

            {/* ── Summary stats ── */}
            <Box>
              <Typography variant="caption" fontWeight={700} color="text.secondary"
                sx={{ textTransform: "uppercase", letterSpacing: 0.6, display: "block", mb: 1.25 }}>
                {t("cyc_report_total")}
              </Typography>
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                <Box sx={{
                  px: 2, py: 1, borderRadius: 3, textAlign: "center",
                  border: "1px solid", borderColor: "divider",
                  minWidth: 70,
                }}>
                  <Typography variant="h5" fontWeight={700} sx={{ color: MENSTRUAL_RED }}>
                    {total}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">{t("cyc_report_total")}</Typography>
                </Box>
                {SEVERITY_LEVELS.map(({ value, key, color }) => (
                  <Box key={value} sx={{
                    px: 2, py: 1, borderRadius: 3, textAlign: "center",
                    border: "1px solid", borderColor: "divider",
                    minWidth: 70,
                  }}>
                    <Typography variant="h5" fontWeight={700}>{bySev[value] || 0}</Typography>
                    <Chip label={t(key)} size="small" color={color}
                      sx={{ height: 18, fontSize: "0.6rem", mt: 0.25 }} />
                  </Box>
                ))}
              </Stack>
            </Box>

            {/* ── By category ── */}
            {topCats.length > 0 && (
              <Box>
                <Typography variant="caption" fontWeight={700} color="text.secondary"
                  sx={{ textTransform: "uppercase", letterSpacing: 0.6, display: "block", mb: 1.25 }}>
                  {t("cyc_report_by_category")}
                </Typography>
                <Stack spacing={0.75}>
                  {topCats.map(([cat, count]) => {
                    const pct = Math.round((count / total) * 100);
                    return (
                      <Stack key={cat} direction="row" alignItems="center" spacing={1.5}>
                        <Typography variant="body2" sx={{ minWidth: 90, textTransform: "capitalize" }}>
                          {t(CAT_KEYS[cat] || cat)}
                        </Typography>
                        <Box sx={{ flex: 1, height: 6, borderRadius: 3,
                          bgcolor: isDark ? "greydark.C800" : "grey.C200",
                          overflow: "hidden" }}>
                          <Box sx={{ width: `${pct}%`, height: "100%", borderRadius: 3,
                            bgcolor: alpha(MENSTRUAL_RED, 0.65) }} />
                        </Box>
                        <Typography variant="caption" color="text.secondary" sx={{ minWidth: 32, textAlign: "right" }}>
                          ×{count}
                        </Typography>
                      </Stack>
                    );
                  })}
                </Stack>
              </Box>
            )}

            <Divider />

            {/* ── Cycle groups ── */}
            <Stack spacing={2.5}>
              {cycleGroups.map(({ cycle, symptoms: cs }) => (
                <Box key={cycle.id}>
                  {/* Cycle header */}
                  <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                    <Typography variant="subtitle2" fontWeight={700}>
                      {t("cyc_report_cycle_from")} {cycle.cycle_start_date?.format("DD MMM YYYY") ?? "—"}
                    </Typography>
                    {cycle.is_active && (
                      <Chip label={t("cyc_status_active")} size="small" color="primary"
                        sx={{ height: 18, fontSize: "0.6rem" }} />
                    )}
                    <Typography variant="caption" color="text.secondary" sx={{ ml: "auto !important" }}>
                      {cs.length} ×
                    </Typography>
                  </Stack>

                  {/* Symptom rows */}
                  <Stack spacing={0.5}>
                    {cs.map((s) => {
                      const phaseInfo  = getPhaseForDate(s.date);
                      const phaseName  = phaseInfo?.phase ?? null;
                      const pColor     = phaseName ? phaseColor(phaseName) : theme.palette.divider;
                      const sev        = SEVERITY_LEVELS.find((l) => l.value === s.severity);
                      return (
                        <Stack key={s.id} direction="row" alignItems="flex-start" spacing={1.5} sx={{
                          px: 1.5, py: 0.75, borderRadius: 2,
                          borderLeft: `4px solid ${pColor}`,
                          bgcolor: isDark ? alpha("#fff", 0.02) : alpha(pColor, 0.04),
                        }}>
                          <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap" useFlexGap>
                              <Typography variant="body2" fontWeight={600}>{s.symptom_name}</Typography>
                              <Chip label={t(CAT_KEYS[s.category] || s.category)} size="small"
                                variant="outlined" sx={{ height: 18, fontSize: "0.6rem", "& .MuiChip-label": { px: 0.75 } }} />
                              {sev && (
                                <Chip label={t(sev.key)} size="small" color={sev.color}
                                  sx={{ height: 18, fontSize: "0.6rem", "& .MuiChip-label": { px: 0.75 } }} />
                              )}
                            </Stack>
                            {s.notes && (
                              <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.25 }}>
                                {s.notes}
                              </Typography>
                            )}
                          </Box>
                          <Stack alignItems="flex-end" sx={{ flexShrink: 0 }}>
                            <Typography variant="caption" color="text.secondary" sx={{ fontVariantNumeric: "tabular-nums" }}>
                              {s.date?.format("DD MMM")}
                            </Typography>
                            {phaseName && (
                              <Typography variant="caption" sx={{ color: pColor, fontWeight: 600, textTransform: "capitalize" }}>
                                {phaseName}
                              </Typography>
                            )}
                          </Stack>
                        </Stack>
                      );
                    })}
                  </Stack>
                </Box>
              ))}
            </Stack>

          </Stack>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 1.5 }}>
        <ButtonsMain name={t("cyc_report_close")} action={onClose} sx={glassButtonSx(isDark)} />
      </DialogActions>
    </Dialog>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Cycle Card
// ─────────────────────────────────────────────────────────────────────────────

function CycleCard({ cycle, idx, statistics, onRefresh, notify }) {
  const theme = useTheme();
  const { t } = useTranslation();
  const phaseColors = usePhaseColors();
  const isDark = theme.palette.mode === "dark";
  const menstrualColor = isDark ? MENSTRUAL_RED_TEXT_DARK : MENSTRUAL_RED;
  const today = dayjs();

  const [expanded,           setExpanded]           = useState(cycle.is_active);
  const [adjustMode,         setAdjustMode]         = useState(false);
  const [endMode,            setEndMode]            = useState(false);
  const [deleteConfirm,      setDeleteConfirm]      = useState(false);
  const [saving,             setSaving]             = useState(false);
  const [symptomDialogPhase, setSymptomDialogPhase] = useState(null); // key of open phase

  const [periodForm, setPeriodForm] = useState({
    startDate: cycle.cycle_start_date?.format("YYYY-MM-DD")   || "",
    endDate:   cycle.menstrual_end_date?.format("YYYY-MM-DD") || "",
  });

  const [newPeriodDate, setNewPeriodDate] = useState(dayjs().format("YYYY-MM-DD"));

  // Derive phase data from cycle
  const phases = PHASE_CONFIG.map(({ key, labelKey, startKey, endKey }) => {
    const start = cycle[startKey];
    const end   = cycle[endKey];
    const days  = start && end ? end.diff(start, "day") + 1 : null;
    const isCurrent = start && end
      ? !today.isBefore(start) && !today.isAfter(end)
      : false;
    return { key, label: t(labelKey), start, end, days, isCurrent };
  });

  const statusLabel = cycle.is_system_prediction ? t("cyc_status_predicted")
    : cycle.is_active ? t("cyc_status_active")
    : t("cyc_status_completed");

  const handleSavePeriod = async () => {
    setSaving(true);
    try {
      // Start date lives on the cycle; end date is a phase boundary adjustment
      await Promise.all([
        axios.patch(`${API_URL}/api/cycles/${cycle.id}/`, {
          cycle_start_date: periodForm.startDate,
        }),
        axios.patch(`${API_URL}/api/cycles/${cycle.id}/adjust-phases/`, {
          menstrual_end_date: periodForm.endDate,
        }),
      ]);
      notify(t("cyc_notify_period_updated"), "success");
      setAdjustMode(false);
      onRefresh();
    } catch (err) {
      notify(err.response?.data?.detail || t("cyc_notify_period_update_fail"), "error");
    } finally { setSaving(false); }
  };

  const handleNewPeriod = async () => {
    setSaving(true);
    try {
      await axios.post(`${API_URL}/api/cycles/`, {
        cycle_start_date:     newPeriodDate,
        period_length:        statistics?.average_period_length        || cycle.period_length        || 4,
        average_cycle_length: statistics?.average_cycle_length         || cycle.average_cycle_length || 28,
        ovulation_start_day:  statistics?.average_ovulation_day        || cycle.ovulation_start_day  || undefined,
      });
      notify(t("cyc_notify_period_started"), "success");
      setEndMode(false);
      onRefresh();
    } catch (err) {
      notify(err.response?.data?.detail || t("cyc_notify_period_start_fail"), "error");
    } finally { setSaving(false); }
  };

  const handleDelete = async () => {
    setSaving(true);
    try {
      await axios.delete(`${API_URL}/api/cycles/${cycle.id}/`);
      notify(t("cyc_notify_cycle_deleted"), "success");
      onRefresh();
    } catch (err) {
      notify(err.response?.data?.detail || t("cyc_notify_cycle_delete_fail"), "error");
      setDeleteConfirm(false);
    } finally { setSaving(false); }
  };

  // ── Card shell
  return (
    <Box sx={{
      mb: 2,
      borderRadius: 4,
      overflow: "hidden",
      bgcolor: isDark ? alpha("#fff", 0.04) : alpha("#fff", 0.5),
      backdropFilter: "blur(28px) saturate(180%)",
      WebkitBackdropFilter: "blur(28px) saturate(180%)",
      border: `1px solid ${isDark ? "rgba(255,255,255,0.08)" : "rgba(255,255,255,0.65)"}`,
      boxShadow: isDark
        ? "inset 0 1px 0 rgba(255,255,255,0.06), 0 8px 32px rgba(0,0,0,0.55)"
        : "inset 0 1px 0 rgba(255,255,255,0.7), 0 4px 24px rgba(0,0,0,0.06)",
      ...(cycle.is_active && !cycle.is_system_prediction && {
        boxShadow: isDark
          ? `0 0 0 1px ${alpha(theme.palette.primary.main, 0.25)}, inset 0 1px 0 rgba(255,255,255,0.06), 0 4px 16px rgba(0,0,0,0.4)`
          : `0 0 0 1px ${alpha(theme.palette.primary.main, 0.15)}, inset 0 1px 0 rgba(255,255,255,0.7), 0 2px 8px rgba(0,0,0,0.06)`,
      }),
    }}>

      {/* ── Header (always visible) ──────────────────────────── */}
      <Box
        onClick={() => setExpanded((v) => !v)}
        sx={{
          px: 2.5, py: 1.5,
          cursor: "pointer",
          display: "flex",
          alignItems: "center",
          gap: 2,
          bgcolor: isDark ? "greydark.C800" : "grey.C50",
          transition: "background-color 0.15s",
          "&:hover": {
            bgcolor: isDark ? "greydark.C700" : "grey.C100",
          },
        }}
      >
        {/* Expand toggle */}
        <Box sx={{ flexShrink: 0, color: "text.secondary" }}>
          {expanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
        </Box>

        {/* Main content */}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          {/* Status row */}
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap sx={{ mb: 0.75 }}>
            <Chip
              label={statusLabel}
              size="small"
              color={cycle.is_system_prediction ? "warning" : cycle.is_active ? "primary" : "default"}
              variant={cycle.is_system_prediction ? "outlined" : "filled"}
              sx={{ height: 20, fontSize: "0.65rem" }}
            />
            {cycle.current_cycle_length ? (
              <Chip label={`${cycle.current_cycle_length}d`} size="small" variant="outlined" sx={{ height: 18, fontSize: "0.6rem" }} />
            ) : cycle.cycle_day ? (
              <Chip label={`Day ${cycle.cycle_day}`} size="small" variant="outlined"
                sx={{ height: 18, fontSize: "0.6rem", bgcolor: alpha(theme.palette.primary.main, 0.1) }} />
            ) : null}
          </Stack>

          {/* Phase timeline bar */}
          <Box sx={{ mb: 1 }}>
            <PhaseTimelineBar cycle={cycle} phaseColors={phaseColors} height={8} />
          </Box>

          {/* Menstrual phase highlight */}
          {(() => {
            const m = phases.find((p) => p.key === "menstrual");
            if (!m?.start) return null;
            return (
              <Stack direction="row" spacing={1} alignItems="center">
                <Box sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: MENSTRUAL_RED, flexShrink: 0 }} />
                <Typography variant="body2" fontWeight={700} sx={{ color: menstrualColor }}>
                  {t("cyc_period")}
                </Typography>
                <Typography variant="body2" sx={{ color: menstrualColor, fontVariantNumeric: "tabular-nums" }}>
                  {m.start.format("DD MMM")} – {m.end?.format("DD MMM") ?? "—"}
                </Typography>
                {m.days && (
                  <Chip
                    label={`${m.days}d`}
                    size="small"
                    sx={{
                      height: 18,
                      fontSize: "0.6rem",
                      bgcolor: alpha(MENSTRUAL_RED, isDark ? 0.15 : 0.1),
                      border: `1px solid ${alpha(MENSTRUAL_RED, 0.35)}`,
                      color: menstrualColor,
                      fontWeight: 700,
                      "& .MuiChip-label": { px: 0.75 },
                    }}
                  />
                )}
              </Stack>
            );
          })()}
        </Box>
      </Box>

      {/* ── Expanded content ─────────────────────────────────── */}
      <Collapse in={expanded} unmountOnExit>
        <Box sx={{ px: 2.5, py: 2 }}>

          {/* Phase details */}
          <Typography variant="caption" fontWeight={700} color="text.secondary"
            sx={{ textTransform: "uppercase", letterSpacing: 0.6, display: "block", mb: 1.25 }}>
            {t("cyc_admin_phase_details")}
          </Typography>

          <Stack spacing={0.25} sx={{ mb: 2 }}>
            {phases.map(({ key, label, start, end, days, isCurrent }) => (
              <Stack key={key} direction="row" alignItems="center" spacing={1.5} sx={{
                px: 1.5, py: 0.75, borderRadius: 2,
                bgcolor: isCurrent ? alpha(phaseColors[key], isDark ? 0.1 : 0.07) : "transparent",
              }}>
                <Box sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: key === "menstrual" ? MENSTRUAL_RED : phaseColors[key], flexShrink: 0 }} />
                <Typography variant="body2" fontWeight={isCurrent ? 700 : 400}
                  sx={{ minWidth: 80, textTransform: "capitalize", color: key === "menstrual" ? menstrualColor : "text.primary" }}>
                  {label}
                </Typography>
                <Stack direction="row" alignItems="center" spacing={1.5} sx={{ flex: 1 }}>
                  <Typography variant="body2" color="text.secondary" sx={{ fontVariantNumeric: "tabular-nums" }}>
                    {start?.format("DD MMM YYYY") ?? "—"}
                    {" → "}
                    {end?.format("DD MMM YYYY") ?? "—"}
                  </Typography>
                  {key === "menstrual" && !cycle.is_system_prediction && (
                    <Tooltip title={t("cyc_admin_edit_period_tooltip")}>
                      <IconButton size="small" onClick={() => setAdjustMode((v) => !v)}
                        sx={{ color: menstrualColor, "&:hover": { bgcolor: alpha(MENSTRUAL_RED, isDark ? 0.15 : 0.08) } }}>
                        <Pencil size={13} />
                      </IconButton>
                    </Tooltip>
                  )}
                </Stack>
                <Chip label={days ? `${days}d` : "—"} size="small" variant="outlined"
                  sx={{ fontSize: "0.6rem", height: 20, minWidth: 34, "& .MuiChip-label": { px: 0.75 } }} />
                {isCurrent && (
                  <Chip label={t("cyc_today")} size="small"
                    sx={{ height: 26, fontSize: "0.75rem", fontWeight: 700, bgcolor: alpha(phaseColors[key], 0.25), "& .MuiChip-label": { px: 1.25 } }} />
                )}
                {!cycle.is_system_prediction && start && end && (
                  <Tooltip title={t("cyc_symptom_log_tooltip")}>
                    <IconButton size="small" onClick={() => setSymptomDialogPhase(key)}
                      sx={{ color: "text.secondary", "&:hover": { color: phaseColors[key] } }}>
                      <NotebookPen size={13} />
                    </IconButton>
                  </Tooltip>
                )}
              </Stack>
            ))}
          </Stack>

          {/* Edit period form — opens when pencil is clicked */}
          {!cycle.is_system_prediction && (
            <>
              {adjustMode && (
                <Box sx={{
                  p: 2, mb: 2, borderRadius: 3,
                  border: `1px solid ${alpha(MENSTRUAL_RED, 0.3)}`,
                  bgcolor: alpha(MENSTRUAL_RED, isDark ? 0.06 : 0.03),
                }}>
                  <Typography variant="caption" fontWeight={700}
                    sx={{ color: menstrualColor, textTransform: "uppercase", letterSpacing: 0.6, display: "block", mb: 1.5 }}>
                    {t("cyc_admin_edit_period_title")}
                  </Typography>
                  <Grid container spacing={1.5} alignItems="center">
                    <Grid size={{ xs: 12, sm: 4 }}>
                      <TextField
                        type="date"
                        label={t("cyc_admin_start_date")}
                        size="small"
                        fullWidth
                        InputLabelProps={{ shrink: true }}
                        inputProps={{ max: dayjs().format("YYYY-MM-DD") }}
                        value={periodForm.startDate}
                        onChange={(e) => setPeriodForm({ ...periodForm, startDate: e.target.value })}
                      />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 4 }}>
                      <TextField
                        type="date"
                        label={t("cyc_admin_end_date")}
                        size="small"
                        fullWidth
                        InputLabelProps={{ shrink: true }}
                        inputProps={{ max: dayjs().format("YYYY-MM-DD") }}
                        value={periodForm.endDate}
                        onChange={(e) => setPeriodForm({ ...periodForm, endDate: e.target.value })}
                      />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 4 }}>
                      <Box sx={{ px: 1.5, py: 1, borderRadius: 2, border: "1px solid", borderColor: "divider", textAlign: "center" }}>
                        <Typography variant="caption" color="text.secondary" display="block">{t("cyc_admin_duration")}</Typography>
                        <Typography variant="subtitle2" fontWeight={700} sx={{ color: menstrualColor }}>
                          {periodForm.startDate && periodForm.endDate
                            ? `${dayjs(periodForm.endDate).diff(dayjs(periodForm.startDate), "day") + 1}d`
                            : "—"}
                        </Typography>
                      </Box>
                    </Grid>
                  </Grid>
                  <Stack direction="row" spacing={1} justifyContent="flex-end" sx={{ mt: 1.5 }}>
                    <ButtonsMain name={t("cyc_cancel")} action={() => setAdjustMode(false)} sx={glassButtonSx(isDark)} />
                    <ButtonsMain
                      name={saving ? t("cyc_saving") : t("cyc_admin_save_period")}
                      action={handleSavePeriod}
                      disabled={saving || !periodForm.startDate || !periodForm.endDate}
                      sx={glassButtonSx(isDark)}
                    />
                  </Stack>
                </Box>
              )}
            </>
          )}

          {/* Actions — End Cycle + Delete */}
          {!cycle.is_system_prediction && (
            <>
              <Divider sx={{ my: 2 }} />
              <Stack direction="row" spacing={1} alignItems="flex-start" flexWrap="wrap" useFlexGap>

                {/* New period — active cycles only */}
                {cycle.is_active && (
                  <>
                    {!endMode ? (
                      <ButtonsMain
                        name={t("cyc_admin_new_period_btn")}
                        action={() => setEndMode(true)}
                        startIcon={<Plus size={14} />}
                        sx={glassButtonSx(isDark)}
                      />
                    ) : (
                      <Box sx={{
                        p: 2, borderRadius: 3, width: "100%",
                        border: "1px solid", borderColor: alpha(MENSTRUAL_RED, 0.3),
                        bgcolor: alpha(MENSTRUAL_RED, isDark ? 0.06 : 0.03),
                      }}>
                        <Typography variant="caption" fontWeight={700} display="block"
                          sx={{ color: menstrualColor, mb: 0.5 }}>
                          {t("cyc_admin_new_period_btn")}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1.5 }}>
                          {t("cyc_admin_new_period_desc")}
                        </Typography>
                        <Stack direction={{ xs: "column", sm: "row" }} spacing={1} alignItems={{ sm: "center" }}>
                          <TextField
                            type="date"
                            label={t("cyc_admin_period_start_date")}
                            size="small"
                            InputLabelProps={{ shrink: true }}
                            inputProps={{ max: dayjs().format("YYYY-MM-DD") }}
                            value={newPeriodDate}
                            onChange={(e) => setNewPeriodDate(e.target.value)}
                            sx={{ flex: 1 }}
                          />
                          <Stack direction="row" spacing={1}>
                            <ButtonsMain name={t("cyc_cancel")} action={() => setEndMode(false)} sx={glassButtonSx(isDark)} />
                            <ButtonsMain
                              name={saving ? t("cyc_saving") : t("cyc_confirm")}
                              action={handleNewPeriod}
                              disabled={!newPeriodDate || saving}
                              sx={glassButtonSx(isDark)}
                            />
                          </Stack>
                        </Stack>
                      </Box>
                    )}
                  </>
                )}

                {/* Delete */}
                <Box sx={{ ml: cycle.is_active ? "auto !important" : 0 }}>
                  {!deleteConfirm ? (
                    <Tooltip title={t("cyc_admin_delete_tooltip")}>
                      <IconButton size="small" color="error" onClick={() => setDeleteConfirm(true)}>
                        <Trash2 size={16} />
                      </IconButton>
                    </Tooltip>
                  ) : (
                    <Box sx={{
                      p: 1.5, borderRadius: 2,
                      bgcolor: alpha(theme.palette.error.main, 0.06),
                      border: "1px solid", borderColor: alpha(theme.palette.error.main, 0.2),
                    }}>
                      <Typography variant="caption" fontWeight={600} color="error.main" display="block" sx={{ mb: 1 }}>
                        {t("cyc_admin_delete_confirm")}
                      </Typography>
                      <Stack direction="row" spacing={1}>
                        <ButtonsMain name={t("cyc_cancel")} action={() => setDeleteConfirm(false)} sx={glassButtonSx(isDark)} />
                        <ButtonsMain name={saving ? "…" : t("cyc_delete")} action={handleDelete} disabled={saving} sx={glassButtonSx(isDark)} />
                      </Stack>
                    </Box>
                  )}
                </Box>
              </Stack>
            </>
          )}

        </Box>
      </Collapse>

      {/* Phase symptom dialogs */}
      {phases.map(({ key, label, start, end }) => (
        <PhaseSymptomDialog
          key={key}
          open={symptomDialogPhase === key}
          onClose={() => setSymptomDialogPhase(null)}
          cycle={cycle}
          phaseKey={key}
          phaseLabel={label}
          phaseStart={start}
          phaseEnd={end}
        />
      ))}
    </Box>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Main export
// ─────────────────────────────────────────────────────────────────────────────

export default function CycleAdministration() {
  const { cycles, statistics, loading, loadAll } = useCycleDataContext();
  const { t } = useTranslation();

  const [reportOpen, setReportOpen] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });

  const notify = (message, severity = "success") => setSnackbar({ open: true, message, severity });
  const handleRefresh = () => loadAll();

  // Active on top, then newest → oldest
  const sortedCycles = [...cycles].filter((c) => !c.is_system_prediction).sort((a, b) => {
    if (a.is_active) return -1;
    if (b.is_active) return  1;
    return (b.cycle_start_date?.valueOf() || 0) - (a.cycle_start_date?.valueOf() || 0);
  });

  if (loading) {
    return <Box sx={{ p: 8, display: "flex", justifyContent: "center" }}><CircularProgress /></Box>;
  }

  return (
    <Box>

      {/* Header */}
      <Stack direction="row" justifyContent="space-between" alignItems="flex-start" sx={{ mb: 3 }}>
        <Box>
          <Typography variant="h6" fontWeight={700}>{t("cyc_admin_title")}</Typography>
          <Typography variant="body1" color="text.secondary">
            {t("cyc_admin_subtitle")}
          </Typography>
        </Box>
        <Tooltip title={t("cyc_report_tooltip")}>
          <IconButton onClick={() => setReportOpen(true)} sx={{ mt: 0.5 }}>
            <Logs size={20} />
          </IconButton>
        </Tooltip>
      </Stack>

      <SymptomsReportDialog open={reportOpen} onClose={() => setReportOpen(false)} />

      {/* Cycle cards */}
      {sortedCycles.length === 0 ? (
        <Box sx={{
          py: 6, textAlign: "center",
          border: "1px dashed", borderColor: "divider", borderRadius: 4,
        }}>
          <Calendar size={32} style={{ opacity: 0.3, marginBottom: 8 }} />
          <Typography variant="body1" color="text.secondary">
            {t("cyc_admin_empty")}
          </Typography>
        </Box>
      ) : (
        sortedCycles.map((cycle, idx) => (
          <CycleCard
            key={cycle.id}
            cycle={cycle}
            idx={idx}
            statistics={statistics}
            onRefresh={handleRefresh}
            notify={notify}
          />
        ))
      )}


      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert severity={snackbar.severity} sx={{ borderRadius: 2 }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
